import { Icons } from '@/assets/icons/icons';
import { Button, Tooltip, XStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { useTranscriptSelection } from './use-transcript-selection';

export const TranscriptBulkActions = ({
  selection,
  canDelete,
}: {
  selection: ReturnType<typeof useTranscriptSelection>;
  canDelete: boolean;
}) => {
  if (!selection.selectedCount) return null;

  return (
    <XStack
      className="absolute inset-x-0 bottom-[max(0.25rem,calc(var(--chat-dock-inset,0px)-3.5rem))] z-10 justify-center pointer-events-none"
      data-find-exclude
    >
      <XStack
        role="group"
        aria-label="Transcript selection actions"
        className="items-center gap-2 pointer-events-auto"
      >
        <Button
          size="sm"
          variant="secondary"
          onClick={selection.clearSelection}
          disabled={selection.isDeleting}
          className="gap-1.5 border border-border-strongest dark:bg-surface dark:hover:enabled:bg-surface-hover"
          aria-label="Clear transcript selection"
        >
          <span aria-live="polite">{selection.selectedCount} selected</span>
          <Icons.cancel className="size-3 text-foreground-muted" />
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={selection.copySelection}
          disabled={selection.isDeleting}
          className="gap-1.5 border border-border-strongest dark:bg-surface dark:hover:enabled:bg-surface-hover"
        >
          <Icons.copy className="size-3.5 text-foreground-muted" />
          Copy
        </Button>
        {canDelete && (
          <Tooltip label="Delete selected chunks">
            <Button
              size="sm"
              variant={selection.confirmingDelete ? 'destructive' : 'secondary'}
              onClick={selection.deleteSelection}
              disabled={selection.isDeleting}
              isLoading={selection.isDeleting}
              className={cn(
                'gap-1.5 border border-border-strongest',
                !selection.confirmingDelete &&
                  'dark:bg-surface dark:hover:enabled:bg-surface-hover'
              )}
            >
              <Icons.trash
                className={cn(
                  'size-3.5',
                  !selection.confirmingDelete && 'text-foreground-muted'
                )}
              />
              {selection.confirmingDelete ? 'Confirm?' : 'Delete'}
            </Button>
          </Tooltip>
        )}
      </XStack>
    </XStack>
  );
};
